import { useCallback, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ReviewFilterParams, ReviewFilterSortOptions, ReviewType } from "@/services/bedrock";
import type {
  ReviewDatePreset,
  ReviewSentimentFilter,
  ReviewSourceFilterValue,
  ReviewTriageFilter,
} from "@/components/reviews";
import { resolveSourceSystemQueryParam } from "@/components/reviews";
import {
  CoachReviewFilterState,
  CoachReviewUrlState,
  CoachNpsCategoryFilter,
  CoachRecommendationFilter,
  DEFAULT_REVIEW_ORDER,
  DEFAULT_REVIEW_SORT,
  DEFAULT_REVIEW_URL_STATE,
  parseCoachReviewSearch,
  serializeCoachReviewSearch,
} from "./reviewFiltersSearchParams";

export type {
  CoachNpsCategoryFilter,
  CoachRecommendationFilter,
  CoachReviewFilterState,
} from "./reviewFiltersSearchParams";

const RATING_SORTS: ReviewFilterSortOptions[] = ["star_rating", "ten_point_rating", "nps_score"];

function toFilterState(urlState: CoachReviewUrlState): CoachReviewFilterState {
  return {
    search: urlState.search,
    sourceSystem: urlState.sourceSystem,
    ratingType: urlState.ratingType,
    ratingValue: urlState.ratingValue,
    npsCategory: urlState.npsCategory,
    recommendation: urlState.recommendation,
    sentiment: urlState.sentiment,
    triage: urlState.triage,
    datePreset: urlState.datePreset,
    startDate: urlState.startDate,
    endDate: urlState.endDate,
    sort: urlState.sort,
    order: urlState.order,
  };
}

export function useCoachReviewFilters() {
  const history = useHistory();
  const location = useLocation();

  const urlState = useMemo(() => parseCoachReviewSearch(location.search), [location.search]);
  const filters = useMemo(() => toFilterState(urlState), [urlState]);

  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;

  const replaceUrlState = useCallback(
    (updater: (current: CoachReviewUrlState) => CoachReviewUrlState) => {
      const current = parseCoachReviewSearch(locationSearchRef.current);
      const next = updater(current);
      const search = serializeCoachReviewSearch(next);
      const normalized = search ? `?${search}` : "";
      if (normalized === (locationSearchRef.current || "")) return;
      locationSearchRef.current = normalized;
      history.replace({ search: normalized });
    },
    [history]
  );

  const patchFilters = useCallback(
    (patch: Partial<CoachReviewFilterState>) => {
      replaceUrlState(current => ({
        ...current,
        ...patch,
      }));
    },
    [replaceUrlState]
  );

  const setSearch = useCallback(
    (search: string) => {
      patchFilters({ search });
    },
    [patchFilters]
  );

  const setSourceSystem = useCallback(
    (sourceSystem: ReviewSourceFilterValue | null) => {
      patchFilters({ sourceSystem });
    },
    [patchFilters]
  );

  const setSentiment = useCallback(
    (sentiment: ReviewSentimentFilter | null) => {
      patchFilters({ sentiment });
    },
    [patchFilters]
  );

  const setTriage = useCallback(
    (triage: ReviewTriageFilter | null) => {
      patchFilters({ triage });
    },
    [patchFilters]
  );

  const setDatePreset = useCallback(
    (next: { preset: ReviewDatePreset | null; startDate: string | null; endDate: string | null }) => {
      patchFilters({
        datePreset: next.preset,
        startDate: next.startDate,
        endDate: next.endDate,
      });
    },
    [patchFilters]
  );

  const setRatingType = useCallback(
    (ratingType: ReviewType | null) => {
      replaceUrlState(current => {
        const clearingRatingSort =
          current.sort != null && RATING_SORTS.includes(current.sort) && (!ratingType || ratingType === "text");
        return {
          ...current,
          ratingType,
          ratingValue: null,
          npsCategory: null,
          recommendation: null,
          sort: clearingRatingSort ? null : current.sort,
          order: clearingRatingSort ? DEFAULT_REVIEW_ORDER : current.order,
        };
      });
    },
    [replaceUrlState]
  );

  const setRatingValue = useCallback(
    (ratingValue: number | null) => {
      patchFilters({ ratingValue, npsCategory: null, recommendation: null });
    },
    [patchFilters]
  );

  const setNpsCategory = useCallback(
    (npsCategory: CoachNpsCategoryFilter | null) => {
      patchFilters({ npsCategory, ratingValue: null, recommendation: null });
    },
    [patchFilters]
  );

  const setRecommendation = useCallback(
    (recommendation: CoachRecommendationFilter | null) => {
      patchFilters({ recommendation, ratingValue: null, npsCategory: null });
    },
    [patchFilters]
  );

  const toggleSort = useCallback(
    (sort: ReviewFilterSortOptions) => {
      replaceUrlState(current => {
        if (current.sort !== sort) {
          return { ...current, sort, order: "asc" };
        }
        if (current.order === "asc") {
          return { ...current, order: "desc" };
        }
        return { ...current, sort: null, order: DEFAULT_REVIEW_ORDER };
      });
    },
    [replaceUrlState]
  );

  const setPlayerId = useCallback(
    (playerId: number | null) => {
      replaceUrlState(current => ({ ...current, playerId }));
    },
    [replaceUrlState]
  );

  const setSegmentationIds = useCallback(
    (segmentationIds: number[]) => {
      replaceUrlState(current => ({ ...current, segmentationIds }));
    },
    [replaceUrlState]
  );

  const setReviewId = useCallback(
    (reviewId: string | number | null) => {
      replaceUrlState(current => ({
        ...current,
        reviewId: reviewId != null ? String(reviewId) : null,
      }));
    },
    [replaceUrlState]
  );

  const clearFilters = useCallback(() => {
    replaceUrlState(current => ({
      ...DEFAULT_REVIEW_URL_STATE,
      reviewId: current.reviewId,
    }));
  }, [replaceUrlState]);

  const clearPopoverFilters = useCallback(() => {
    replaceUrlState(current => ({
      ...current,
      search: "",
      sourceSystem: null,
      ratingType: null,
      ratingValue: null,
      npsCategory: null,
      recommendation: null,
      sentiment: null,
      triage: null,
      datePreset: null,
      startDate: null,
      endDate: null,
      playerId: null,
      segmentationIds: [],
    }));
  }, [replaceUrlState]);

  const activeFilterCount = useMemo(() => {
    return (
      (filters.sourceSystem ? 1 : 0) +
      (filters.ratingType ? 1 : 0) +
      (filters.ratingValue != null ? 1 : 0) +
      (filters.npsCategory ? 1 : 0) +
      (filters.recommendation ? 1 : 0) +
      (filters.sentiment ? 1 : 0) +
      (filters.triage ? 1 : 0) +
      (filters.datePreset || filters.startDate || filters.endDate ? 1 : 0) +
      (urlState.playerId ? 1 : 0) +
      (urlState.segmentationIds.length ? 1 : 0)
    );
  }, [filters, urlState.playerId, urlState.segmentationIds]);

  const toQueryParams = useCallback((): ReviewFilterParams => {
    const params: ReviewFilterParams = {
      perPage: urlState.perPage,
      search: filters.search || undefined,
      playerId: urlState.playerId ?? undefined,
      segmentationIds: urlState.segmentationIds.length ? urlState.segmentationIds : undefined,
      sourceSystem: resolveSourceSystemQueryParam(filters.sourceSystem),
      sentiment: filters.sentiment ?? undefined,
      sort: filters.sort ?? DEFAULT_REVIEW_SORT,
      order: filters.sort ? filters.order : DEFAULT_REVIEW_ORDER,
      extraFields: { player: ["regionNames", "branchNames"] },
    };

    if (filters.ratingType === "text") {
      if (filters.recommendation) {
        params.sourceSentiment = filters.recommendation;
      } else {
        params.hasSourceSentiment = true;
      }
    } else if (filters.ratingType) {
      params.ratingType = [filters.ratingType];
    }

    if (filters.ratingValue != null && filters.ratingType && filters.ratingType !== "text") {
      if (filters.ratingType === "star") params.starRating = filters.ratingValue;
      if (filters.ratingType === "nps") params.npsScore = filters.ratingValue;
      if (filters.ratingType === "ten_point") params.tenPointRating = filters.ratingValue;
    }

    if (filters.npsCategory && filters.ratingType === "nps") {
      params.npsCategory = filters.npsCategory;
    }

    if (filters.triage === "needingReply") {
      // Uses server-side unreplied + excludes Internal Feedback (IF) reviews
      params.needingReply = true;
    } else if (filters.triage === "replied") {
      params.replied = true;
    } else if (filters.triage === "skipped") {
      params.replySkipped = true;
    }

    if (filters.startDate || filters.endDate) {
      params.startDate = filters.startDate ?? undefined;
      params.endDate = filters.endDate ?? undefined;
    }

    return params;
  }, [filters, urlState.perPage, urlState.playerId, urlState.segmentationIds]);

  return {
    filters,
    playerId: urlState.playerId,
    segmentationIds: urlState.segmentationIds,
    reviewId: urlState.reviewId,
    perPage: urlState.perPage,
    setSearch,
    setSourceSystem,
    setSentiment,
    setTriage,
    setDatePreset,
    setRatingType,
    setRatingValue,
    setNpsCategory,
    setRecommendation,
    toggleSort,
    setPlayerId,
    setSegmentationIds,
    setReviewId,
    clearFilters,
    clearPopoverFilters,
    activeFilterCount,
    toQueryParams,
  };
}

export type UseCoachReviewFiltersReturn = ReturnType<typeof useCoachReviewFilters>;
